import React, { createContext, useContext, useEffect, useState } from "react";
import { useUserEvents } from "../lib/userEvents";
import { api, setAuthToken, getToken, ensureMediaToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  useUserEvents(!!user);

  // Save the browser/phone timezone once authenticated; backend applies it only if the user never set it themselves.
  const detectTz = () => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (!tz) return;
      api.post("/auth/detect-timezone", { timezone: tz })
        .then((r) => { if (r?.data?.applied) syncMe(); })
        .catch(() => {});
    } catch (e) {}
  };

  useEffect(() => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => { setUser(r.data); detectTz(); })
      .catch(() => setAuthToken(null))
      .finally(() => setLoading(false));
  }, []);

  // every auth payload is followed by /auth/me so credit gauges (credits_cap, daily_used) are in sync from the first screen
  const syncMe = () => api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});

  // Apply the account's saved UI language on login (e.g. a new device) without the user re-picking it in Profile.
  useEffect(() => {
    const want = user?.settings?.app_language;
    if (!want || !["id", "en"].includes(want)) return;
    const cur = localStorage.getItem("aivora_lang") || "id";
    if (want !== cur) { localStorage.setItem("aivora_lang", want); window.location.reload(); }
  }, [user]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setAuthToken(r.data.access_token);
    await ensureMediaToken(true);
    setUser(r.data.user);
    syncMe();
    detectTz();
    return r.data.user;
  };

  const register = async (email, password, name) => {
    const r = await api.post("/auth/register", { email, password, name, app_url: window.location.origin });
    if (r.data.access_token) { setAuthToken(r.data.access_token); await ensureMediaToken(true); setUser(r.data.user); syncMe(); detectTz(); }
    return r.data;
  };

  // apply a {access_token, user} payload (email verification, invite acceptance, workspace switch)
  const applyAuth = async (data) => { setAuthToken(data.access_token); await ensureMediaToken(true); setUser(data.user); syncMe(); detectTz(); return data.user; };

  const switchWorkspace = async (workspaceId) => applyAuth((await api.post("/auth/switch-workspace", { workspace_id: workspaceId })).data);

  const logout = async () => {
    const push_token = localStorage.getItem("oryntix_push_token") || undefined;
    try { await api.post("/auth/logout", { push_token }); } catch (e) {}
    localStorage.removeItem("oryntix_push_token");
    setAuthToken(null); setUser(null);
  };

  const refreshUser = async () => {
    try { const r = await api.get("/auth/me"); setUser(r.data); return r.data; } catch (e) {}
  };

  const setCredits = (c) => setUser((u) => (u ? { ...u, credits: c } : u));

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, refreshUser, setCredits, applyAuth, switchWorkspace }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
